import { _ } from "../../../core/polyglot/polyglot.js";
import {
	checkDoubleIcon,
	checkIcon,
} from "../../../core/elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Icon displayed on a period report in the archive to show
 * if the report has been viewed and/or acknowledged.
 * On hovering a report, it tells how many people viewed/acknowledged it.
 *
 * @param {ReportSummary} reportSummary - Report to which to add the icon
 */
export default class ReportStatusIcon extends Widget {
	constructor({ reportSummary }) {
		super(...arguments);
		this._reportSummary = reportSummary;
	}

	renderContentOn(html) {
		const statuses = [];

		if (this._reportSummary.isViewedByCompanyUser()) {
			statuses.push(this._renderViewedOn.bind(this));
		}

		if (this._reportSummary.isAcknowledged()) {
			statuses.push(this._renderAcknowledgedOn.bind(this));
		}

		return html.div(
			{
				klass: "report-statuses",
			},
			statuses,
		);
	}

	_renderViewedOn(html) {
		html.render(
			checkIcon({
				title: this._buildTitleString(
					this._reportSummary.getViewByCompanyUserEvents(),
					{
						singleString: ({ name }) =>
							_("{name} viewed this report", { name }),
						multipleString: ({ name, number }) =>
							_("{name} and {number} others viewed this report", {
								name,
								number,
							}),
						unnamedUserString: () =>
							_("Someone viewed this report"),
					},
				),
			}),
		);
	}

	_renderAcknowledgedOn(html) {
		html.render(
			checkDoubleIcon({
				title: this._buildTitleString(
					this._reportSummary.getAcknowledgeEvents(),
					{
						singleString: ({ name }) =>
							_("{name} acknowledged this report", { name }),
						multipleString: ({ name, number }) =>
							_(
								"{name} and {number} others acknowledged this report",
								{
									name,
									number,
								},
							),
						unnamedUserString: () =>
							_("Someone acknowledged this report"),
					},
				),
			}),
		);
	}

	_buildTitleString(
		events,
		{ singleString, multipleString, unnamedUserString },
	) {
		const lastEvent = events.at(-1);

		if (!lastEvent) {
			return "";
		}

		const author = lastEvent.getAuthor();

		if (author.isUnnamed()) {
			return unnamedUserString();
		}

		const name = author.getName();

		return events.length === 1
			? singleString({ name })
			: multipleString({
					name,
					number: events.length - 1,
				});
	}
}
